import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const categories = ["Materials", "Fuel", "Mileage", "Equipment", "Labor", "Chemicals", "Subcontractor", "Office", "Other"];
const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ date: today(), category: "Materials", vendor: "", amount: 0, notes: "" });

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  const load = () => {
    api.get("/expenses").then((r) => setItems(r.data));
    api.get("/reports/summary").then((r) => setReport(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.amount) return toast.error("Amount is required");
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Expense logged"); setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5 border-border rounded-lg bg-card"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Revenue (Paid)</span><TrendingUp className="h-4 w-4 text-primary" /></div><div className="font-display text-2xl font-black mt-2">{money(report.revenue)}</div></Card>
          <Card className="p-5 border-border rounded-lg bg-card"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Expenses</span><TrendingDown className="h-4 w-4 text-secondary" /></div><div className="font-display text-2xl font-black mt-2">{money(report.expenses)}</div></Card>
          <Card className="p-5 border-border rounded-lg bg-primary text-primary-foreground"><div className="flex items-center justify-between"><span className="overline text-primary-foreground/70">Net Profit</span><Wallet className="h-4 w-4" /></div><div className="font-display text-2xl font-black mt-2">{money(report.profit)}</div></Card>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Track every cost to know your numbers.</p>
        <Button data-testid="add-expense-btn" onClick={() => { setForm(empty()); setOpen(true); }} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"><Plus className="h-4 w-4 mr-1.5" /> Log Expense</Button>
      </div>

      <Card className="border-border rounded-lg overflow-hidden bg-card">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Vendor</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No expenses logged yet.</TableCell></TableRow>
            ) : items.map((e) => (
              <TableRow key={e.id} data-testid="expense-row" className="hover:bg-muted/30 transition-colors duration-200">
                <TableCell className="text-sm">{e.date}</TableCell>
                <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                <TableCell>{e.vendor || "—"}</TableCell>
                <TableCell className="text-muted-foreground text-sm max-w-[16rem] truncate">{e.notes}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(e.amount)}</TableCell>
                <TableCell><div className="flex justify-end"><button data-testid="delete-expense-btn" onClick={() => remove(e.id)} className="p-2 rounded-md hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Log Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Amount ($) *</Label><Input data-testid="expense-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-expense-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
